<template>
    <div class="evaluation">

        <div class="pb-4 text-center">
            <base-button type="white mr-1" size="sm" @click="expandAll">
                <i class="fas fa-chevron-down"></i>
                {{ $t('common.expand-all') }}
            </base-button>
            
            <base-button type="white" size="sm" @click="collapseAll">
                <i class="fas fa-chevron-up"></i>
                {{ $t('common.collapse-all') }}
            </base-button>
        </div>

        <div ref="competencies" v-if="localEvaluation">
            <collapse class="competencies" ref="competencies-collapse">

                <div v-for="(competency, index) in grid.competencies" :key="index" class="pt-4 competency">

                    <collapse-item-card
                        :id="'competency-'+competency.id"
                        :ref="'competency-'+competency.id"
                        :class="progressStyle(competency)"
                        width-style="width-80"
                    >
                    
                        <template slot="title">
                            <h6 class="font-weight-bold text-uppercase mb-0 pl-4">
                                <div class="header-commands float-right">
            
                                    <base-button type="primary" outline
                                        class="btn-icon-only rounded-circle mr-0 mt--2"
                                        @click="editComment(competency)"
                                        v-if="features.canEdit() || hasCompetencyComment(competency.id)"
                                    >
                                        <span class="btn-inner--icon"><i class="fas fa-edit"></i></span>
                                        <span class="badge badge-md badge-circle badge-floating badge-warning no-border"
                                                v-if="hasCompetencyComment(competency.id)">
                                            {{ stats.competencies[competency.id].notes }}
                                        </span>
                                    </base-button>

                                    <span class="grade ml-3"
                                        :class="'bg-' + ratingButtonColor(competency, localEvaluation[competency.id].rating)"
                                        v-if="localEvaluation[competency.id].rating"
                                    >
                                        {{ localEvaluation[competency.id].rating }}
                                    </span>
                
                                </div>
                                {{ competencyName(competency) }}
                            </h6>
                            <div class="comment pl-4 pt-3" v-if="!emptyString(localEvaluation[competency.id].comment)">
                                <div class="description">{{ localEvaluation[competency.id].comment }}</div>
                            </div>
                        </template>

                        <template>
                            <div class="value-stats" style="border-top: 1px solid #dcdfe6">
                                <table class="w-100" height="7px">
                                    <tr class="">
                                        <td :style="'width:'+yesnoProgress(competency.id, 'yes')" class="bg-success"></td>
                                        <td :style="'width:'+yesnoProgress(competency.id, 'no')" class="bg-danger"></td>
                                        <td :style="'width:'+yesnoProgress(competency.id, '-')" class="bg-secondary"></td>
                                        <td></td>
                                    </tr>
                                </table>
                            </div>

                            <div class="font-weight-bolder description pl-4 pb-3 pt-3"
                                style="border-top: 1px solid #dcdfe6"
                            >{{ competencyDescription(competency) }}</div>
                            <div class="pb-2">
                                <table width="100%">
                                    <tr v-for="ratingValue in Object.keys(competency.rating)" :key="ratingValue" class="rating-line">
                                        <td class="pl-4 py-2">
                                            <div class="response description" v-html="competency.rating[ratingValue]"></div>
                                        </td>
                                        <td class="py-2 pr-3 grade-button" width="50">
                                            <base-button
                                                :type="ratingButtonColor(competency, ratingValue)"
                                                @click="selectRating(competency, ratingValue)"
                                                :disabled="!features.canEdit()"
                                            >{{ ratingValue }}</base-button>
                                        </td>
                                    </tr>
                                </table>
                            </div>
                        </template>
                    </collapse-item-card>
                </div>
            </collapse>
        </div>

        <portal to="modals">

            <comment-edit-modal v-model="modals.editComment"
                :comment="currentCompetencyId ? localEvaluation[currentCompetencyId].comment : ''"
                :features="features"
                @change="commentChanged"
            >
            </comment-edit-modal>
            
        </portal>
    </div>
</template>

<script>
    import ModalsController from '@trax/classes/ModalsController'
    import CommentEditModal from './CommentEditModal'

    export default {
        
        components: {
            CommentEditModal
        },

        props: {

            session: {
                type: Object,
                required: true
            },

            grid: {
                type: Object,
                required: true
            },

            competencies: {
                type: Object,
                default: null
            },
            
            evaluation: {
                type: Object,
                default: null
            },

            lang: {
                type: String,
                default: "en"
            },

            features: {
                default: null
            },

            stats: {
                type: Object,
                default: null
            },
        },

        data() {
            return {
                modals: new ModalsController(['editComment']),
                currentCompetencyId: null,
                localEvaluation: null,
                initializing: true,
            }
        },

        created() {
            this.initEvaluation()
        },

        watch: {

            localEvaluation: {
                handler() {
                    if (this.initializing) {
                        this.initializing = false
                        return
                    }
                    this.saveEvaluation()
                },
                deep: true,
            }
        },

        mounted() {
            for (let competencyId in this.localEvaluation) {
                if (this.localEvaluation[competencyId].rating) {
                    this.$refs['competencies-collapse'].collapseItem('competency-' + competencyId)
                }
            }
        },

        methods: {

            yesnoProgress(competencyId, category) {
                const data = this.stats
                    && this.stats.competencies[competencyId].observation
                    && this.stats.competencies[competencyId].observation.yesno

                if (data) {
                    const progress = parseInt(data[category] * 100 / data.total)
                    return progress + '%'
                }

                return '0%'
            },

            competencyName(competency) {
                const nameProp = 'name_' + this.lang
                return competency[nameProp]
            },

            competencyDescription(competency) {
                return competency.description
            },

            hasCompetencyComment(competencyId) {
                return this.stats && this.stats.competencies[competencyId] && this.stats.competencies[competencyId].notes
            },

            initEvaluation() {
                this.localEvaluation = {}
                for (const competency of this.grid.competencies) {
                    if (this.evaluation[competency.id]) {
                        this.localEvaluation[competency.id] = this.evaluation[competency.id]
                    } else {
                        Vue.set(this.localEvaluation, competency.id, {
                            rating: null,
                            comment: null,
                        })
                    }
                }
            },

            emptyString(value) {
                return value == null || value.trim().length == 0
            },

            editComment(competency) {
                this.currentCompetencyId = competency.id
                this.modals.open('editComment')
            },

            commentChanged(value) {
                this.localEvaluation[this.currentCompetencyId].comment = value
            },

            selectRating(competency, ratingValue) {
                if (this.localEvaluation[competency.id].rating == ratingValue) {
                    this.localEvaluation[competency.id].rating = null
                } else {
                    this.localEvaluation[competency.id].rating = ratingValue
                    this.$refs['competencies-collapse'].collapseItem('competency-' + competency.id)
                }
            },

            ratingButtonColor(competency, ratingValue) {
                if (this.localEvaluation[competency.id].rating != ratingValue) {
                    return 'white'
                }
                return ratingValue <= 3 ? 'danger' : 'success'
            },

            progressStyle(competency) {
                if (this.localEvaluation[competency.id].rating == null) {
                    return ''
                }

                return this.localEvaluation[competency.id].rating <= 3
                    ? 'competency-danger'
                    : 'competency-success'
            },

            saveEvaluation() {
                axios.post('/socrates/api/front/dashboard/data/sessions/'+this.session.id+'/evaluation', {
                    data: this.localEvaluation
                })
                .then(resp => {
                    this.$emit('evaluationChanged')
                })
                .catch(err => {
                    this.$auth.handleErrorVm(err, this)
                })        
            },

            expandAll() {
                this.$refs['competencies-collapse'].expandAll();
            },

            collapseAll() {
                this.$refs['competencies-collapse'].collapseAll();
            },
        }
    }
</script>

<style scoped>

    .evaluation .competencies >>> .card {
        margin-top: -20px;
        margin-bottom: 30px;
    }
    .evaluation .competencies >>> .card {
        border: solid 3px #fff;
    }
    .evaluation .competencies >>> .card.competency-danger {
        border: solid 3px #f5365c;
    }
    .evaluation .competencies >>> .card.competency-success {
        border: solid 3px #2dce89;
    }
    .evaluation .competencies >>> .card .description {
        white-space: pre-wrap;
    }

    .evaluation .competencies >>> .card-header {
        border-bottom: 0px solid;
        padding-right: 1.2rem;
    }
    .evaluation .competencies >>> .card-header h6 {
        font-size: 1.1rem;
    }
    .evaluation .competencies >>> .card-header .grade {
        color: white;
        border-radius: 1rem;
        font-weight: normal;
        padding: 6px 12px;
        font-size: 0.9rem;
    }
    .evaluation .competencies >>> .card-header:after {
        right: auto;
        top: 33px
    }

    .evaluation .competencies >>> .card-body {
        padding: 0;
    }
    .evaluation .competencies >>> .card-body .tinymce p {
        font-size: .9rem !important;
    }
    .evaluation .competencies >>> .card-body .rating-line {
        border-top: 1px dashed #dcdfe6;
    }

    .evaluation .competencies >>> .header-commands button {
        z-index: 1000;
        background-color: white;
        border-color: white;
        color: #212529;
    }
    .evaluation .competencies >>> .header-commands button:hover {
        box-shadow: none;
    }
    .evaluation .competencies >>> .header-commands button:active {
        background-color: #d2e3ee;
        border-color: white;
        color: #212529;
    }
    .evaluation .competencies >>> button .badge {
        border: none;
        width: 1.4rem;
        height: 1.4rem;
    }

    .grade-button button:disabled:hover {
        box-shadow: none;
        transform: none;
    }

    .value-stats .bg-secondary {
        background-color: #aaa !important;
    }

</style>
